import React, { useEffect, useState } from "react";
import { Toast, getFormattedOptions } from "utility/helpers";
import {
  Col,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import { Settings } from "react-feather";
import PropTypes from "prop-types";
import CustomSelect from "components/customs/dropdown";
import { fetchAllAgencyProjects } from "services/services.projects";
import {
  fetchUtilizationSetting,
  updateUtilizationSetting,
} from "services/services.utilization";
import { modalDropdownStyles } from "constants/style";
import { AddButton, FormLabel, RequiredIndicator } from "assets/js/Styles";
import styled from "styled-components";
import FormGenerator from "../form-generator/input";

const BtnSave = styled(AddButton)`
  background: ${({ theme }) => theme.colors.buttonBlue};
  height: 45px;
  width: 100px;
`;

const ModalHeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const ModalHeaderText = styled.p`
  margin-bottom: 0;
  margin-left: 0.5rem;
  color: ${({ theme }) => theme.colors.headerTextBlue};
`;

const ModalHeaderBorderLeft = styled(Col)`
  height: 3px;
  background: #00316d;
`;

const ModalHeaderBorder = styled(Row)`
  display: flex;
  width: 100%;
  align-items: center;
  margin: 0;
`;

const FormRow = styled.div`
  margin-top: 1.5rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const StyledModal = styled(Modal)`
  --bs-modal-margin: 0 !important;
  margin-right: 0 !important;

  .modal-dialog {
    position: fixed !important;
    right: 0 !important;
    margin: 0 !important;
  }

  .modal-content {
    overflow: hidden;
    height: 100vh;
    border: none;
    border-radius: 0;

    .modal-body {
      overflow-y: auto;
    }
  }
`;

const StyledModalFooter = styled(ModalFooter)`
  display: flex;
  justify-content: flex-end;
`;

const ModalBodyContainer = styled.div`
  margin-top: 1.5rem;
  margin-right: 0.5rem;
  margin-left: 0.5rem;
`;

const StyledSettingsIcon = styled(Settings)`
  color: ${({ theme }) => theme.colors.iconBlue};
`;

const StyledInput = styled(FormGenerator)`
  height: 45px;
`;

const SettingsModal = ({ isOpen, handleClose, agency, handleFetchTable }) => {
  const [projectOptions, setProjectOptions] = useState();
  const [agencyOptions, setAgencyOptions] = useState();
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [utilPercent, setUtilPercent] = useState();
  const [startDate, setStartDate] = useState();
  const [settingsId, setSettingsId] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [isAgencyChanged, setIsAgencyChanged] = useState(false);

  const fetchSettings = (proj) => {
    fetchUtilizationSetting({
      url: "/utilizationsetting",
      callback: (data) => {
        const settings = data.find(
          (setting) => setting.ProjectId === proj.value
        );
        setUtilPercent(settings.utilizationPercent);
        setStartDate(new Date(settings.reportingStartDate));
        setSettingsId(settings.id);
      },
    });
  };

  const validate = () => {
    if (utilPercent === "") {
      return "Utility Percentage cannot be empty";
    }

    if (!startDate) {
      return "Start Date cannot be empty";
    }

    setIsLoading(false);

    return true;
  };

  const updateSettings = async () => {
    const isValid = await validate();

    if (typeof isValid === "boolean") {
      setIsLoading(true);

      const formData = {
        utilizationPercent: utilPercent,
        reportingStartDate: startDate,
      };

      updateUtilizationSetting({
        url: `/utilizationsetting/${settingsId}`,
        formData,
        callback: ({ isSuccess }) => {
          setIsLoading(false);

          if (isSuccess) {
            setTimeout(() => {
              Toast({
                type: 1,
                content: "Successfully updated utilization setting.",
              });
              handleClose();
            }, 1500);
          } else {
            Toast({
              type: 4,
              content: "An error occurred.",
            });
          }
          handleFetchTable();
        },
      });
    } else {
      Toast({
        type: 4,
        content: isValid,
      });
    }
  };

  const fetchProjects = () => {
    fetchAllAgencyProjects({
      url: `/agency/${selectedAgency?.value}`,
      callback: (data) => {
        let projects = data?.Projects;

        if (selectedAgency?.value === 14) {
          projects = data?.Projects.filter(
            (project) => project.name === "Infrastructure"
          );
        }

        const options = getFormattedOptions("name", projects);
        if (selectedAgency?.value === 14) setSelectedProject(options[0]);
        setProjectOptions(options);
      },
    });
  };

  useEffect(() => {
    if (agency.length !== 0) {
      setAgencyOptions(agency);
    }
  }, [agency]);

  useEffect(() => {
    if (agencyOptions) {
      setSelectedAgency(agencyOptions[0]);
      fetchProjects();
    }
  }, [agencyOptions]);

  useEffect(() => {
    if (selectedProject && !isAgencyChanged) fetchSettings(selectedProject);
  }, [selectedProject]);

  useEffect(() => {
    if (selectedProject && isAgencyChanged) setSelectedProject([]);
    fetchProjects();
  }, [selectedAgency]);

  return (
    <StyledModal
      isOpen={isOpen}
      toggle={() => {
        handleClose();
      }}
    >
      <ModalHeader
        toggle={() => {
          handleClose();
        }}
      >
        <ModalHeaderContainer>
          <StyledSettingsIcon />
          <ModalHeaderText>{"Settings"}</ModalHeaderText>
        </ModalHeaderContainer>
      </ModalHeader>
      <ModalHeaderBorder>
        <ModalHeaderBorderLeft xs="12" />
      </ModalHeaderBorder>

      <ModalBody>
        <ModalBodyContainer>
          <FormRow>
            <div>
              <FormLabel>{"Agency"}</FormLabel>
              <p>{"Provide the Agency"}</p>
            </div>

            <CustomSelect
              placeholder="Agency"
              styles={modalDropdownStyles}
              options={agencyOptions}
              value={selectedAgency}
              onChange={(option) => {
                setSelectedAgency(option);
                setIsAgencyChanged(true);

                setStartDate(null);
                setUtilPercent("");
              }}
              filterOption={(option, searchValue) =>
                option?.label
                  ?.toLowerCase()
                  ?.includes(searchValue?.toLowerCase()) ||
                option?.data?.acronym
                  ?.toLowerCase()
                  ?.includes(searchValue?.toLowerCase())
              }
            />
          </FormRow>

          <FormRow>
            <div>
              <FormLabel>{"Program"}</FormLabel>
              <p>{"Provide the Program"}</p>
            </div>

            <CustomSelect
              placeholder="Program"
              styles={modalDropdownStyles}
              options={projectOptions}
              value={selectedProject}
              onChange={(option) => {
                setSelectedProject(option);
                setIsAgencyChanged(false);
              }}
              isDisabled={
                selectedAgency === null || selectedAgency?.value === 14
              }
            />
          </FormRow>

          <FormRow>
            <div>
              <FormLabel>
                {"Start Date"}
                {RequiredIndicator}
              </FormLabel>
              <p>{"Provide the start date"}</p>
            </div>

            <StyledInput
              type="datetime"
              selected={startDate}
              placeholderText="Start Date"
              dateFormat="MMM-dd-yyyy"
              onChange={(date) => {
                setStartDate(date);
              }}
            />
          </FormRow>

          <FormRow>
            <div>
              <FormLabel>
                {"Utilization Percentage"}
                {RequiredIndicator}
              </FormLabel>
              <p>{"Provide utilization percentage"}</p>
            </div>

            <StyledInput
              type="number"
              placeholder="%"
              value={utilPercent}
              onValueChange={(event) => {
                const { value } = event;

                setUtilPercent(value);
              }}
            />
          </FormRow>
        </ModalBodyContainer>
      </ModalBody>

      <StyledModalFooter>
        <BtnSave
          color="primary"
          onClick={() => updateSettings()}
          disabled={isLoading}
        >
          {isLoading ? (
            <Spinner
              animation="border"
              color="light"
              size="sm"
            />
          ) : (
            "Save"
          )}
        </BtnSave>
      </StyledModalFooter>
    </StyledModal>
  );
};

SettingsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  agency: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object])),
  handleFetchTable: PropTypes.func,
};

SettingsModal.defaultProps = {
  agency: [],
};

export default SettingsModal;
